import { useCallback, useLayoutEffect, useState } from 'react'
import { scrollsHorizontally, showsStickyEdge } from '@/lib/stickyColumn'

/**
 * Tracks whether a table's right-pinned column currently has content hidden
 * underneath it, so the pinned cells can draw their leading edge only then.
 *
 * Takes a ref for the `<table>` and finds the scroll container itself rather
 * than being handed one, because the caller usually does not own it: the tables
 * here nest two candidates — the page's own `overflow-x-auto` wrapper and the
 * one `components/ui/table.tsx` renders inside it — and which of the two
 * actually moves depends on how wide the content is. Asking the DOM which one
 * scrolls is reliable in a way that naming one is not.
 *
 * Returns a callback ref so the search runs the moment the table attaches; a
 * `useRef` is still null on the effect pass after the first render of a table
 * that renders conditionally.
 */
export function useStickyColumnEdge<T extends HTMLElement>(): {
  ref: (node: T | null) => void
  showEdge: boolean
} {
  const [node, setNode] = useState<T | null>(null)
  const [showEdge, setShowEdge] = useState(false)

  const findScroller = useCallback((from: T): HTMLElement | null => {
    let el: HTMLElement | null = from.parentElement
    while (el && el !== document.body) {
      if (scrollsHorizontally({ overflowX: getComputedStyle(el).overflowX, scrollWidth: el.scrollWidth, clientWidth: el.clientWidth })) {
        return el
      }
      el = el.parentElement
    }
    return null
  }, [])

  useLayoutEffect(() => {
    if (!node) return

    // Re-resolved on every update, not captured once: a table that fits has no
    // scrolling ancestor at all, and one appears the moment a column is widened
    // or the window narrows. Caching the answer from mount would leave those
    // tables permanently edgeless.
    const update = () => {
      const scroller = findScroller(node)
      setShowEdge(scroller ? showsStickyEdge(scroller) : false)
    }
    update()

    const scroller = findScroller(node)
    scroller?.addEventListener('scroll', update, { passive: true })
    // Watches the table, not the scroller: a column resize changes the table's
    // width while the container's stays put, and that is exactly the moment the
    // edge needs to appear or go away.
    const observer = new ResizeObserver(update)
    observer.observe(node)
    if (scroller) observer.observe(scroller)
    window.addEventListener('resize', update)

    return () => {
      scroller?.removeEventListener('scroll', update)
      observer.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [node, findScroller])

  return { ref: setNode, showEdge }
}
